import {
  AsYouType,
  validatePhoneNumberLength,
  getCountryCallingCode,
} from 'libphonenumber-js/min'

const KG_CODE = '996'
const KG_DIGITS = 9
const MAX_DIGITS = 15 // максимум по E.164
const MIN_DIGITS = 6 // цифр в поле вместе с кодом страны; меньше — обрывок, а не номер
const DEFAULT_VALUE = `+${KG_CODE} `


const TEXTS = {
  ru: {
    errorEmpty: 'Введите номер телефона',
    errorShort: 'Введите номер полностью',
    hintKg: 'Проверьте номер: обычно это +996 и 9 цифр',
    hintCountry: 'Не удалось определить страну — проверьте код',
    hintCheck: 'Проверьте номер — возможно, в нём опечатка',
  },
  ky: {
    errorEmpty: 'Телефон номериңизди киргизиңиз',
    errorShort: 'Номерди толук киргизиңиз',
    hintKg: 'Номерди текшериңиз: адатта +996 жана 9 сан',
    hintCountry: 'Өлкө коду аныкталган жок — текшериңиз',
    hintCheck: 'Номерди текшериңиз — ката болушу мүмкүн',
  },
}

const REGION_OVERRIDES = {
  ru: { KG: 'Кыргызстан' },
}

const getLang = (input) => {
  const form = input.closest('form')

  return form?.dataset.formLanguage || document.documentElement.lang
}

const getTexts = (input) => TEXTS[getLang(input)] || TEXTS.ru

const getFlag = (country) => country.replace(/./g, (letter) => String.fromCodePoint(
  0x1F1E6 + letter.charCodeAt(0) - 'A'.charCodeAt(0),
))

const getCountryName = (country, lang) => {
  const override = REGION_OVERRIDES[lang]?.[country]

  if (override) return override

  try {
    return new Intl.DisplayNames([lang], { type: 'region' }).of(country) || country
  } catch (e) {
    return country
  }
}

const getCountry = (digits) => {
  const formatter = new AsYouType()

  formatter.input(`+${digits}`)

  return formatter.country
}


const normalize = (value) => {
  let digits = value.replace(/\D/g, '')

  if (digits.startsWith(KG_CODE + KG_CODE)) {
    digits = digits.slice(KG_CODE.length)
  }

  return digits.slice(0, MAX_DIGITS)
}


const format = (value) => {
  const digits = normalize(value)

  if (!digits) return '+'

  return new AsYouType().input(`+${digits}`)
}

// Курсор привязываем к порядковому номеру цифры: пробелы маски на этот счёт не влияют
const caretAfterDigit = (value, digitNumber) => {
  if (digitNumber <= 0) return 1 // за «+» курсор не пускаем

  let seen = 0

  for (let i = 0; i < value.length; i += 1) {
    if (value[i] >= '0' && value[i] <= '9') {
      seen += 1

      if (seen === digitNumber) {
        return i + 1
      }
    }
  }

  return value.length
}

const countDigits = (value) => value.replace(/\D/g, '').length


const getStatus = (input) => {
  const digits = normalize(input.value)
  const country = getCountry(digits)
  const national = country ? digits.slice(getCountryCallingCode(country).length) : digits
  const texts = getTexts(input)
  const empty = !national.length

  if (empty) return { empty, blocking: true, message: texts.errorEmpty }

  if (digits.length < MIN_DIGITS) return { empty, blocking: true, message: texts.errorShort }

  if (country === 'KG' && national.length !== KG_DIGITS) {
    return { empty, blocking: false, message: texts.hintKg }
  }

  if (!country) return { empty, blocking: false, message: texts.hintCountry }

  if (validatePhoneNumberLength(`+${digits}`)) {
    return { empty, blocking: false, message: texts.hintCheck }
  }

  return { empty, blocking: false, message: null }
}

const setMessage = (input, message, blocking) => {
  const container = input.closest('.input-field-container')

  if (!container) return

  container.querySelector('.js-phone-error')?.remove()
  input.classList.toggle('is-error', Boolean(message) && blocking)

  if (!message) return

  const text = document.createElement('div')
  text.className = blocking
    ? 'input-field-helper-text is-danger'
    : 'input-field-helper-text is-muted'
  text.textContent = message

  const line = document.createElement('div')
  line.className = 'input-field-helper-line js-phone-error'
  line.append(text)

  container.append(line)
}

const showValidity = (input) => {
  const { message, blocking } = getStatus(input)

  setMessage(input, message, blocking)

  return !blocking
}

const showCountry = (input) => {
  const label = input.closest('.input-field-container')?.querySelector('.js-phone-country')

  if (!label) return

  const country = getCountry(normalize(input.value))

  label.textContent = country
    ? `${getFlag(country)} ${getCountryName(country, getLang(input))}`
    : ''
}

const initInput = (input) => {
  const container = input.closest('.input-field-container')

  if (!container || input.dataset.phoneReady) return

  input.dataset.phoneReady = '1'
  input.setAttribute('inputmode', 'tel')
  input.value = input.value ? format(input.value) : DEFAULT_VALUE

  const label = document.createElement('div')
  label.className = 'phone-country js-phone-country'

  container.append(label)
  showCountry(input)

  input.addEventListener('keydown', (e) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return

    const { selectionStart, selectionEnd } = input

    if (selectionStart !== selectionEnd) {
      if (selectionStart < 1) {
        input.setSelectionRange(1, Math.max(selectionEnd, 1))
      }

      return
    }

    const touchesPlus = e.key === 'Backspace' ? selectionStart <= 1 : selectionStart < 1

    if (touchesPlus) {
      e.preventDefault()
    }
  })

  input.addEventListener('input', () => {
    const digitsBeforeCaret = countDigits(input.value.slice(0, input.selectionStart))
    const formatted = format(input.value)

    if (formatted !== input.value) {
      input.value = formatted

      const caret = caretAfterDigit(formatted, digitsBeforeCaret, 1)

      input.setSelectionRange(caret, caret)
    }

    showCountry(input)

    if (container.querySelector('.js-phone-error')) {
      showValidity(input)
    }
  })

  input.addEventListener('blur', () => {
    const { empty, message, blocking } = getStatus(input)

    setMessage(input, empty ? null : message, blocking)
  })
}

const phoneInputs = document.querySelectorAll('input[type="tel"]')

phoneInputs.forEach(initInput)

// Блокируем отправку до того, как отработает обработчик в form-check.js
document.querySelectorAll('[data-form-name]').forEach(form => {
  const formPhones = form.querySelectorAll('input[type="tel"]')

  if (!formPhones.length) return

  form.addEventListener('submit', (e) => {
    let hasError = false

    formPhones.forEach(input => {
      if (!showValidity(input)) {
        hasError = true
      }
    })

    if (hasError) {
      e.preventDefault()
      e.stopImmediatePropagation()
      formPhones[0].focus()
    }
  })
})
